import { loadScript } from 'dashboard/helper/DOMHelpers';

export const loadFacebookSdk = async () => {
  return loadScript('https://connect.facebook.net/en_US/sdk.js', {
    async: true,
    defer: true,
    crossOrigin: 'anonymous',
  });
};

// WASLO: Bounds for the readiness poll below.
const FB_READY_POLL_INTERVAL_MS = 50;
const FB_READY_POLL_TIMEOUT_MS = 30000;

export const initializeFacebook = (appId, apiVersion) => {
  const version = apiVersion || 'v22.0';
  return new Promise((resolve, reject) => {
    // WASLO: `connect.facebook.net/en_US/sdk.js` is a TWO-STAGE loader. Executing it
    // WASLO: installs a PARTIAL `window.FB` (no `login`), then it fetches
    // WASLO: `en_US/bundle/sdk.js/` and only then installs the real object. Upstream's
    // WASLO: `if (window.FB)` therefore passes on the stub: measured against the live
    // WASLO: SDK, `FB.login` only became a function 26 ms AFTER the script's `load`
    // WASLO: event, so `FB.init()` ran against the stub and callers went straight on to
    // WASLO: `FB.login(...)` — a TypeError inside a promise nobody settles, which is one
    // WASLO: of the two ways the Embedded Signup button span forever. Readiness is
    // WASLO: `FB.login` being callable, not `FB` merely existing.
    // WASLO: This function is shared by every Meta SDK flow (WhatsApp signup, WhatsApp
    // WASLO: reauthorize, Facebook pages), so it may only ever resolve LATER than
    // WASLO: upstream did, never earlier.
    let settled = false;
    let pollId = null;

    const isSdkReady = () => typeof window.FB?.login === 'function';

    const init = () => {
      if (settled || !isSdkReady()) return;
      settled = true;
      if (pollId) clearInterval(pollId);
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version,
      });
      resolve();
    };

    if (isSdkReady()) {
      init();
      return;
    }

    // WASLO: Keep upstream's `fbAsyncInit` path — Meta invokes it once the real SDK is
    // WASLO: installed — but CHAIN any existing hook instead of clobbering it, so two
    // WASLO: concurrent flows (Facebook pages + WhatsApp) can't cancel each other out.
    const previousAsyncInit = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      // WASLO: the other flow's hook is not ours to trust; a throw in it must not
      // WASLO: take our init down with it.
      try {
        if (typeof previousAsyncInit === 'function') previousAsyncInit();
      } catch {
        // the other flow owns its own failure
      }
      init();
    };

    // WASLO: `fbAsyncInit` fires exactly once. If it already fired before we hooked it
    // WASLO: (a second caller, or a preload that raced this one), the hook above would
    // WASLO: never run and this promise would hang forever — so poll for readiness too.
    // WASLO: When the poll gives up it must REJECT, not quietly stop: a pending-forever
    // WASLO: promise poisons `useWhatsappEmbeddedSignup`'s memo (its `.catch` never
    // WASLO: runs, so every later attempt awaits the same dead promise) and pins
    // WASLO: `Reauthorize.vue`'s `isLoadingFacebook` on. Every caller handles a
    // WASLO: rejection already.
    const maxAttempts = Math.ceil(
      FB_READY_POLL_TIMEOUT_MS / FB_READY_POLL_INTERVAL_MS
    );
    let attempts = 0;
    pollId = setInterval(() => {
      attempts += 1;
      if (isSdkReady()) {
        init();
      } else if (attempts >= maxAttempts) {
        clearInterval(pollId);
        pollId = null;
        settled = true;
        reject(new Error('Facebook SDK never became ready'));
      }
    }, FB_READY_POLL_INTERVAL_MS);
  });
};

export const isValidBusinessData = businessData => {
  return businessData && businessData.business_id && businessData.waba_id;
};

export const createMessageHandler = onEmbeddedSignupData => {
  return event => {
    if (!event.origin.endsWith('facebook.com')) return;

    try {
      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else if (typeof event.data === 'object' && event.data !== null) {
        data = event.data;
      } else {
        return;
      }

      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        onEmbeddedSignupData(data);
      }
    } catch {
      // Ignore non-JSON or irrelevant messages
    }
  };
};

export const initWhatsAppEmbeddedSignup = configId => {
  return new Promise((resolve, reject) => {
    window.FB.login(
      response => {
        if (response.authResponse && response.authResponse.code) {
          resolve(response.authResponse.code);
        } else if (response.error) {
          reject(new Error(response.error));
        } else {
          reject(new Error('Login cancelled'));
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          // WASLO: `featureType` KEPT — it is still supported in Embedded Signup v4 and
          // WASLO: is what selects WhatsApp Business-app ("coexistence") onboarding,
          // WASLO: which this fork now completes (`config/waslo/whatsapp_coexistence.rb`).
          // WASLO: Removing it would silently drop every business back to the plain
          // WASLO: Cloud-API flow.
          featureType: 'whatsapp_business_app_onboarding',
          // WASLO: `sessionInfoVersion: '3'` REMOVED. The session-info version is set by
          // WASLO: the Facebook Login for Business *configuration*, and both Waslo
          // WASLO: environments point at the v4 config (1398478792216017). The
          // WASLO: `sessionInfoVersion` extra is a v2-only key (it appears on no v3/v4
          // WASLO: page of Meta's docs) and v2 is deprecated in Oct 2026, so passing it
          // WASLO: is at best inert and at worst pins a deprecated payload shape.
          // WASLO: Guarded by `specs/utils.spec.js` in this directory.
        },
      }
    );
  });
};

export const setupFacebookSdk = async (appId, apiVersion) => {
  const version = apiVersion || 'v22.0';
  await loadFacebookSdk();
  await initializeFacebook(appId, version);
};
